// src/components/LabTopology.jsx
// Topologie réseau interactive en SVG
import React, { useState, useCallback, useRef } from "react";
import { Box, Typography, Chip, alpha } from "@mui/material";

// ── Icônes SVG des équipements ───────────────────────────────────────────────
const DeviceIcon = ({ type, size = 40, active = false }) => {
  const color = active ? "#58a6ff" : "#8b949e";
  const bg    = active ? alpha("#58a6ff", 0.15) : alpha("#8b949e", 0.08);

  const icons = {
    router: (
      <g>
        <circle cx="20" cy="20" r="16" fill={bg} stroke={color} strokeWidth="1.5" />
        <circle cx="20" cy="20" r="8"  fill={color} opacity="0.3" />
        <circle cx="20" cy="20" r="3"  fill={color} />
        {[0,60,120,180,240,300].map((a,i) => {
          const r = 11;
          const rad = (a * Math.PI) / 180;
          return <circle key={i} cx={20 + r*Math.cos(rad)} cy={20 + r*Math.sin(rad)} r="2" fill={color} />;
        })}
        {[0,60,120,180,240,300].map((a,i) => {
          const r1 = 8; const r2 = 13;
          const rad = (a * Math.PI) / 180;
          return <line key={i} x1={20+r1*Math.cos(rad)} y1={20+r1*Math.sin(rad)} x2={20+r2*Math.cos(rad)} y2={20+r2*Math.sin(rad)} stroke={color} strokeWidth="1.5" />;
        })}
      </g>
    ),
    switch: (
      <g>
        <rect x="4" y="12" width="32" height="16" rx="3" fill={bg} stroke={color} strokeWidth="1.5" />
        {[9,14,19,24,29,31].map((x, i) => (
          <g key={i}>
            <rect x={x} y="16" width="3" height="8" rx="1" fill={color} opacity="0.5" />
            {i % 2 === 0
              ? <path d={`M${x+1.5} 22 L${x+1.5} 25`} stroke={color} strokeWidth="1" />
              : <path d={`M${x+1.5} 25 L${x+1.5} 22`} stroke={color} strokeWidth="1" />
            }
          </g>
        ))}
        <circle cx="33" cy="15" r="2" fill={active ? "#3fb950" : "#8b949e"} />
        <circle cx="36" cy="15" r="2" fill={active ? "#f85149" : "#8b949e"} />
      </g>
    ),
    pc: (
      <g>
        <rect x="6" y="8"  width="28" height="19" rx="2" fill={bg} stroke={color} strokeWidth="1.5" />
        <rect x="9" y="11" width="22" height="13" rx="1" fill={color} opacity="0.15" />
        <rect x="14" y="27" width="12" height="3"  rx="1" fill={bg} stroke={color} strokeWidth="1" />
        <rect x="10" y="30" width="20" height="2"  rx="1" fill={bg} stroke={color} strokeWidth="1" />
        {active && <rect x="10" y="12" width="3" height="2" rx="0.5" fill={color} opacity="0.8" />}
      </g>
    ),
    server: (
      <g>
        <rect x="8" y="4"  width="24" height="34" rx="3" fill={bg} stroke={color} strokeWidth="1.5" />
        {[8,14,20,26].map((y, i) => (
          <g key={i}>
            <rect x="10" y={y+2} width="20" height="4" rx="1" fill={color} opacity="0.15" />
            <circle cx="27" cy={y+4} r="1.5" fill={i === 0 ? "#3fb950" : color} opacity="0.8" />
          </g>
        ))}
      </g>
    ),
    cloud: (
      <g>
        <path d="M10 28 Q6 28 6 22 Q6 16 12 16 Q12 10 20 10 Q28 10 28 16 Q34 16 34 22 Q34 28 30 28 Z" fill={bg} stroke={color} strokeWidth="1.5" />
      </g>
    ),
    firewall: (
      <g>
        <path d="M20 4 L36 12 L36 28 L20 36 L4 28 L4 12 Z" fill={bg} stroke={color} strokeWidth="1.5" />
        <path d="M20 10 L28 14 V22 L20 28 L12 22 V14 Z" fill={color} opacity="0.2" />
        <path d="M16 20 Q18 15 20 18 Q22 12 24 18 Q22 21 20 25 Q18 21 16 20Z" fill={color} opacity="0.7" />
      </g>
    ),
  };

  return (
    <svg width={size} height={size} viewBox="0 0 40 40" style={{ display: "block" }}>
      {icons[type] || icons.router}
    </svg>
  );
};

// ── Composant principal ───────────────────────────────────────────────────────
export default function LabTopology({
  devices = [],    // [{id, type, x, y, label?, config?}]
  links   = [],    // [{from, to, label?}]
  activeDevice = null,
  onSelectDevice = null,
  onDeviceMove   = null,   // (deviceId, {x, y}) => void — si fourni, active le drag & drop
  width = "100%",
  height = 320,
  showLabels = true,
}) {
  const [hover, setHover]   = useState(null);
  const [dragId, setDragId] = useState(null);   // id de l'appareil en cours de déplacement
  const containerRef        = useRef(null);
  const ICON_SIZE = 48;
  const HALF = ICON_SIZE / 2;
  const draggable = Boolean(onDeviceMove);

  // ── Drag & drop (actif uniquement si onDeviceMove est fourni) ──────────────
  // Le déplacement se fait en suivant le pointeur, avec les coordonnées
  // relatives au conteneur SVG — pas besoin de librairie externe, juste
  // pointerdown/pointermove/pointerup sur le Box de chaque appareil.
  const handlePointerDown = useCallback((e, devId) => {
    if (!draggable) return;
    e.preventDefault();
    e.stopPropagation();
    setDragId(devId);
  }, [draggable]);

  const handlePointerMove = useCallback((e) => {
    if (!dragId || !containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    // Centre l'appareil sous le pointeur (au lieu de son coin haut-gauche)
    const x = Math.round(e.clientX - rect.left - HALF);
    const y = Math.round(e.clientY - rect.top  - HALF);
    // Empêche de sortir du cadre visible
    const clampedX = Math.max(0, Math.min(x, rect.width  - ICON_SIZE));
    const clampedY = Math.max(0, Math.min(y, rect.height - ICON_SIZE));
    onDeviceMove(dragId, { x: clampedX, y: clampedY });
  }, [dragId, onDeviceMove, HALF]);

  const handlePointerUp = useCallback(() => {
    setDragId(null);
  }, []);

  // Calculer les positions de lien (centre → centre)
  const getLinkPath = useCallback((link) => {
    const from = devices.find(d => d.id === link.from);
    const to   = devices.find(d => d.id === link.to);
    if (!from || !to) return null;
    const fx = from.x + HALF;
    const fy = from.y + HALF;
    const tx = to.x   + HALF;
    const ty = to.y   + HALF;
    return { fx, fy, tx, ty };
  }, [devices]);

  // Couleur du lien selon statut des équipements
  const linkColor = (link) => {
    const f = devices.find(d => d.id === link.from);
    const t = devices.find(d => d.id === link.to);
    if (f?.id === activeDevice || t?.id === activeDevice) return "#58a6ff";
    return "#30363d";
  };

  return (
    <Box
      ref={containerRef}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerUp}
      onPointerLeave={handlePointerUp}
      sx={{
      background: "#0d1117",
      borderRadius: "10px",
      border: "1px solid #21262d",
      overflow: "hidden",
      position: "relative",
      height,
      touchAction: draggable ? "none" : "auto",
    }}>
      {/* Grille de fond */}
      <svg
        width="100%"
        height="100%"
        style={{ position: "absolute", inset: 0 }}
      >
        <defs>
          <pattern id="grid" width="30" height="30" patternUnits="userSpaceOnUse">
            <path d="M 30 0 L 0 0 0 30" fill="none" stroke="#161b22" strokeWidth="0.5" />
          </pattern>
        </defs>
        <rect width="100%" height="100%" fill="url(#grid)" />

        {/* Liens */}
        {links.map((link, i) => {
          const pos = getLinkPath(link);
          if (!pos) return null;
          const { fx, fy, tx, ty } = pos;
          const mx = (fx + tx) / 2;
          const my = (fy + ty) / 2;
          return (
            <g key={i}>
              <line
                x1={fx} y1={fy} x2={tx} y2={ty}
                stroke={linkColor(link)}
                strokeWidth="2"
                strokeDasharray={link.dashed ? "6 4" : undefined}
              />
              {link.label && (
                <text
                  x={mx} y={my - 6}
                  textAnchor="middle"
                  fill="#8b949e"
                  fontSize="10"
                  fontFamily="monospace"
                >
                  {link.label}
                </text>
              )}
            </g>
          );
        })}
      </svg>

      {/* Équipements (HTML au-dessus du SVG) */}
      {devices.map(dev => {
        const isActive   = dev.id === activeDevice;
        const isHovered  = dev.id === hover;
        return (
          <Box
            key={dev.id}
            onClick={() => onSelectDevice?.(dev.id)}
            onPointerDown={(e) => handlePointerDown(e, dev.id)}
            onMouseEnter={() => setHover(dev.id)}
            onMouseLeave={() => setHover(null)}
            sx={{
              position: "absolute",
              left: dev.x,
              top:  dev.y,
              width: ICON_SIZE,
              cursor: draggable ? (dragId === dev.id ? "grabbing" : "grab") : (onSelectDevice ? "pointer" : "default"),
              userSelect: "none",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 0.5,
              transform: isHovered ? "scale(1.1)" : "scale(1)",
              transition: dragId === dev.id ? "none" : "transform 0.15s",
              zIndex: isActive || dragId === dev.id ? 10 : 1,
            }}
          >
            {/* Halo sélection */}
            {isActive && (
              <Box sx={{
                position: "absolute",
                top: -6, left: -6,
                width: ICON_SIZE + 12,
                height: ICON_SIZE + 12,
                borderRadius: "50%",
                border: "2px solid #58a6ff",
                boxShadow: "0 0 12px rgba(88,166,255,.4)",
                pointerEvents: "none",
              }} />
            )}

            <DeviceIcon type={dev.type} size={ICON_SIZE} active={isActive || isHovered} />

            {showLabels && (
              <Typography sx={{
                fontSize: "0.7rem",
                fontFamily: "monospace",
                color: isActive ? "#58a6ff" : "#8b949e",
                fontWeight: isActive ? 700 : 400,
                textAlign: "center",
                whiteSpace: "nowrap",
                lineHeight: 1.2,
              }}>
                {dev.id}
                {dev.label && (
                  <Box component="span" sx={{ display: "block", fontSize: "0.62rem", color: "#6e7681" }}>
                    {dev.label}
                  </Box>
                )}
              </Typography>
            )}

            {/* Config IP si disponible */}
            {isActive && dev.config?.ip && (
              <Chip
                label={dev.config.ip}
                size="small"
                sx={{
                  fontSize: "0.6rem",
                  height: 16,
                  background: alpha("#58a6ff", 0.15),
                  color: "#79c0ff",
                  fontFamily: "monospace",
                  "& .MuiChip-label": { px: .75 },
                }}
              />
            )}
          </Box>
        );
      })}
    </Box>
  );
}